import { useId, useMemo, useState, type KeyboardEvent } from 'react';
import { Caption1, makeStyles } from '@fluentui/react-components';
import { MAX_SERVICE_PLACES, PLACE_INPUT_MAX, PLACE_INPUT_MIN, findVirginiaPlace, searchVirginiaPlaces, type PlaceKind, type ServicePlace, type VirginiaPlace } from '@vigeo/shared';
import { tokens } from '@vigeo/ui';
import { PillField } from './PillField.js';

/**
 * The service-area picker (ADR-021 sections 2 and 3, #593; Figma "Concept A v2" frames 6, 8 and 9): ONE field that suggests
 * Virginia cities or ZIP code areas as HR types, and holds the places chosen as pills inside it. Several per person, at most
 * `MAX_SERVICE_PLACES`.
 *
 * IT ASKS NOBODY. The suggestions come from the list shipped in `@vigeo/shared` (`searchVirginiaPlaces`), filtered here in the
 * browser. This file imports nothing that makes a request: what is typed stays in this component's state.
 *
 * ONE FIELD. Digits find ZIP code areas whose number begins with them; anything else finds cities, towns and communities.
 * A ZIP+4 tail is ignored, because the list holds five-digit areas.
 *
 * WHAT A PICK PUTS ON THE RECORD: our code for the place, its kind and the time of the pick. Never the name or the position:
 * those are read from the list each time (`servicePlaceLabel`).
 *
 * A PLACE SAVED BEFORE THE LIST EXISTED holds one of Google's ids, which is in no list of ours. It reads `PICK_PLACE_AGAIN`
 * and can be removed; nothing is asked of anyone to name it.
 */

/** @deprecated Shown nowhere since #593. It stays only because `index.ts` still names it; #600 removes both. */
export const GOOGLE_ATTRIBUTION = 'Powered by Google';

/** What a place reads when its code is not in the list: one of Google's ids, saved before ADR-021. */
export const PICK_PLACE_AGAIN = 'Pick this place again';

/** Digits only, with an optional ZIP+4 tail, look for ZIP code areas; anything else looks for cities. */
export function placeKindOfText(text: string): PlaceKind {
  return /^(\d{1,5}|\d{5}-\d{0,4})$/.test(text.trim()) ? 'zip' : 'city';
}

/** What the list is searched for: the text as typed, except that a ZIP+4 tail is dropped ("23220-12" searches "23220"). */
const searchText = (typed: string): string => {
  const letters = typed.trim();
  return /^\d{5}-\d{0,4}$/.test(letters) ? letters.slice(0, 5) : letters;
};

/** The suggestions for what is in the box. Fewer than `PLACE_INPUT_MIN` characters suggest nothing. */
export function suggestPlaces(typed: string): VirginiaPlace[] {
  const letters = searchText(typed);
  return letters.length < PLACE_INPUT_MIN ? [] : searchVirginiaPlaces(letters);
}

/** The small tag at the right of a row: the kind. */
export const SUGGESTION_TAG: Readonly<Record<PlaceKind, string>> = { city: 'City', zip: 'ZIP code' };

/**
 * What a suggestion shows. A city reads "Arlington, VA", and two places that share a name carry their county in the list's own
 * name ("Rose Hill (Lee County), VA"), so no two rows read alike. A ZIP code area reads "ZIP 22030".
 */
export function suggestionWords(place: VirginiaPlace): { readonly main: string; readonly tag: string } {
  return { main: place.kind === 'zip' ? `ZIP ${place.name}` : `${place.name}, VA`, tag: SUGGESTION_TAG[place.kind] };
}

/** The words for a place on a record: the list's name for its code ("ZIP" plus the number for a ZIP code area), or `PICK_PLACE_AGAIN`. */
export function servicePlaceLabel(place: Pick<ServicePlace, 'placeId'>): string {
  const listed = findVirginiaPlace(place.placeId);
  if (listed === undefined) return PICK_PLACE_AGAIN;
  return listed.kind === 'zip' ? `ZIP ${listed.name}` : listed.name;
}

const useStyles = makeStyles({
  root: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS },
  label: { fontWeight: tokens.fontWeightSemibold, fontSize: tokens.fontSizeBase300, lineHeight: tokens.lineHeightBase300 },
  list: {
    // In the flow, not floating: inside a dialog a floating list is cut off at the dialog's edge (seen in the pictures), so the dialog grows instead.
    listStyleType: 'none', margin: `${tokens.spacingVerticalXS} 0 0`, padding: 0,
    backgroundColor: tokens.colorNeutralBackground1, border: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke2}`, borderRadius: tokens.borderRadiusMedium,
    maxHeight: '16rem', overflowY: 'auto',
  },
  group: { listStyleType: 'none', margin: 0, padding: 0 },
  groupName: { paddingBlock: tokens.spacingVerticalXS, paddingInline: tokens.spacingHorizontalM, color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase200, fontWeight: tokens.fontWeightSemibold },
  option: { display: 'flex', justifyContent: 'space-between', gap: tokens.spacingHorizontalM, paddingBlock: tokens.spacingVerticalS, paddingInline: tokens.spacingHorizontalM, cursor: 'pointer', color: tokens.colorNeutralForeground1 },
  active: { backgroundColor: tokens.colorNeutralBackground1Hover },
  tag: { color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase200, whiteSpace: 'nowrap' },
  quiet: { color: tokens.colorNeutralForeground3 },
  error: { color: tokens.colorPaletteRedForeground1 },
});

export interface PlacePickerProps {
  readonly value: readonly ServicePlace[];
  readonly onChange: (next: readonly ServicePlace[]) => void;
}

export function PlacePicker({ value, onChange }: PlacePickerProps) {
  const styles = useStyles();
  const ids = useId();
  const listId = `${ids}-list`;
  const [text, setText] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [status, setStatus] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  const full = value.length >= MAX_SERVICE_PLACES;
  const suggestions = useMemo(() => (full ? [] : suggestPlaces(text)), [text, full]);
  const shown = open && suggestions.length > 0;
  const labels = value.map(servicePlaceLabel);

  const onType = (typed: string) => {
    setText(typed); setProblem(null); setActive(-1);
    const found = suggestPlaces(typed);
    setOpen(found.length > 0);
    setStatus(searchText(typed).length < PLACE_INPUT_MIN ? '' : found.length === 0 ? 'No places found.' : `${found.length} ${found.length === 1 ? 'suggestion' : 'suggestions'}. Use the up and down arrow keys, then Enter.`);
  };

  const choose = (place: VirginiaPlace) => {
    if (value.some((p) => p.placeId === place.code)) { setProblem('That place is already on the list.'); setOpen(false); return; }
    setText(''); setOpen(false); setActive(-1); setProblem(null);
    // The code, the kind and the time of the pick. The name and the position stay in the list.
    onChange([...value, { placeId: place.code, kind: place.kind, checkedAt: new Date().toISOString() }]);
    setStatus(`${servicePlaceLabel({ placeId: place.code })} added.`);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      if (suggestions.length === 0) return;
      event.preventDefault();
      setOpen(true);
      setActive((current) => (event.key === 'ArrowDown' ? (current + 1) % suggestions.length : (current <= 0 ? suggestions.length - 1 : current - 1)));
    } else if (event.key === 'Enter') {
      // Enter belongs to the search box while there is something typed: it picks, and never submits the form behind it.
      if (text.trim() !== '') event.preventDefault();
      const target = shown ? suggestions[active] : undefined;
      if (target !== undefined) choose(target);
    } else if (event.key === 'Escape' && shown) {
      event.preventDefault(); event.stopPropagation();
      setOpen(false); setActive(-1);
    }
  };

  const remove = (placeId: string) => { onChange(value.filter((p) => p.placeId !== placeId)); setStatus('Place removed.'); };
  const anyOld = labels.some((label) => label === PICK_PLACE_AGAIN);
  const groupName = suggestions[0]?.kind === 'zip' ? 'ZIP codes' : 'Cities';

  return (
    <div className={styles.root}>
      <span className={styles.label} id={`${ids}-label`}>Service areas</span>
      <PillField
        pillsLabel="Chosen places"
        removeLabel={(label) => (label === PICK_PLACE_AGAIN ? 'Remove the place that has to be picked again' : `Remove ${label}`)}
        pills={value.map((place, i) => ({ key: place.placeId, label: labels[i] ?? '', onRemove: () => remove(place.placeId) }))}
        disabled={full}
        input={{
          value: text,
          placeholder: full ? `Up to ${MAX_SERVICE_PLACES} places` : value.length === 0 ? 'Type a city or ZIP code' : 'Add another',
          maxLength: PLACE_INPUT_MAX,
          autoComplete: 'off',
          role: 'combobox',
          'aria-labelledby': `${ids}-label`,
          'aria-autocomplete': 'list',
          'aria-expanded': shown,
          'aria-controls': listId,
          'aria-activedescendant': shown && active >= 0 ? `${ids}-opt-${active}` : undefined,
          onChange: (event) => onType(event.target.value),
          onKeyDown,
          onBlur: () => setOpen(false),
        }}
      >
        {shown ? (
          <ul id={listId} role="listbox" aria-label="Suggested places" className={styles.list}>
            <li role="presentation">
              <ul role="group" aria-label={groupName} className={styles.group}>
                <li role="presentation" className={styles.groupName}>{groupName}</li>
                {suggestions.map((place, i) => {
                  const words = suggestionWords(place);
                  return (
                    <li
                      key={place.code}
                      id={`${ids}-opt-${i}`}
                      role="option"
                      aria-selected={i === active}
                      aria-label={words.main}
                      className={`${styles.option} ${i === active ? styles.active : ''}`}
                      // Pick before the box loses focus; a click would otherwise arrive after the list closed.
                      onMouseDown={(event) => { event.preventDefault(); choose(place); }}
                    >
                      <span>{words.main}</span>
                      <span className={styles.tag}>{words.tag}</span>
                    </li>
                  );
                })}
              </ul>
            </li>
          </ul>
        ) : null}
      </PillField>
      <div role="status" aria-live="polite" className={styles.quiet}><Caption1>{status}</Caption1></div>
      {problem === null ? null : <div role="alert" className={styles.error}><Caption1>{problem}</Caption1></div>}
      <Caption1 className={styles.quiet}>
        {value.length === 0
          ? 'Type a city or ZIP code in Virginia, then pick it. Add as many as this person covers.'
          : full
            ? `${value.length} places, the most one person can have. Press × on a pill to remove one.`
            : `${value.length} ${value.length === 1 ? 'place' : 'places'}. Press × on a pill to remove it, or type to add another.`}
      </Caption1>
      {anyOld ? <Caption1 className={styles.quiet}>“{PICK_PLACE_AGAIN}” is a place saved before the app had its own list of places. Press × on it, then search for the place and pick it. These service areas cannot be saved while it is there.</Caption1> : null}
    </div>
  );
}
